import { clsx } from 'clsx'

import { ArrowDropDownFilledIcon } from '@/components/icons/ArrowDropDownFilled'
import { Icon } from '@/components/ui/icon/Icon/Icon'
import { InputValue } from '@/types'

import { InputSelectSingleProps } from './type'
import { heights, texts, widths, rounds, errorClassNames } from '../../const'

export const InputSelectSingle = <T extends InputValue = InputValue>({
  ref,
  buttonLabel,
  height = 'lg',
  rounded = 'all',
  selected,
  label,
  name,
  isError,
  className = '',
  handleChange,
  handleFocus,
  handleBlur,
  tabIndex,
  ...props
}: InputSelectSingleProps<T>) => {
  const selectedValue = selected ? String(selected.value) : ''

  const onChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (props.disabled) return
    const option = props.options.find(
      (opt) => String(opt.value) === e.target.value
    )
    if (option) handleChange(option)
  }

  return (
    <div className={clsx([widths['full'], className])}>
      {/* Screen-reader label (replaces old InputSelectLabel) */}
      {label && (
        <label className="tw-invisible tw-absolute tw-h-0 tw-w-0">
          {label}
        </label>
      )}

      <div className="tw-relative">
        <select
          ref={ref}
          name={name}
          value={selectedValue}
          onChange={onChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={props.disabled}
          tabIndex={tabIndex}
          className={clsx([
            'tw-w-full tw-appearance-none',
            heights[height],
            texts[height],
            rounds[rounded],
            'tw-border tw-border-gray-300 tw-bg-white',
            'tw-py-1.5 tw-px-2',
            'tw-text-sm',
            'focus:tw-border-accentBlue-500 focus:tw-outline-none',
            props.disabled &&
              'tw-cursor-not-allowed tw-bg-gray-100 tw-opacity-70',
            isError && !props.disabled && errorClassNames,
          ])}
        >
          {!selected && <option value="">{buttonLabel}</option>}

          {props.options.map((option, index) => (
            <option
              key={`input-select-option_${name || ''}_${index}_${option.value}`}
              value={String(option.value)}
              disabled={option.disabled}
            >
              {option.label}
            </option>
          ))}
        </select>

        {/* Chevron overlay — native arrow is hidden by appearance-none */}
        <span className="tw-absolute tw-inset-y-0 tw-right-0 tw-pointer-events-none tw-flex tw-items-center tw-px-2">
          <Icon
            icon={ArrowDropDownFilledIcon}
            size="xl"
            color="description"
            aria-hidden="true"
          />
        </span>
      </div>
    </div>
  )
}
